import {
  COLLECTION_GROUP_TYPE,
  TRAIN_MODEL_STATUS,
} from "../../../utils/constants";
import {
  getCategories,
  generateCollection,
  getCollections,
  getExploreCollections,
  toggleToFavorite,
  getUserFavorites,
  deleteCollection,
  hideCollection,
  hideCollectionVip,
  getTrainModels,
  getTrainModel,
  createTrainModel,
  updateTrainModel,
  deleteTrainModel,
  fetchTopCreators,
  regenerateCollection,
  rateModel,
  fetchDefaultGallery,
  selectCoverForTrainModel,
  setCollectionPublicCover,
  submitTrainModelForReview,
  getFolders,
  createFolder,
  putCollectionToFolder,
  deleteFolder,
  generateImageCollection,
  regenerateImageCollection,
  enchantPrompt,
  editFolder,
  generateAnonymize,
  fetchAnonymizedCollections,
  selectCollectionsInCollectionGroup,
  fetchCollection,
} from "./asyncThunks";

const updateCollectionInList = (collections, collectionId, updater) => {
  if (!Array.isArray(collections)) {
    return;
  }

  const collectionIndex = collections.findIndex(
    (collection) => collection._id === collectionId,
  );

  if (collectionIndex === -1) {
    return;
  }

  collections[collectionIndex] = updater(collections[collectionIndex]);
};

const removeCollectionMetadataByName = (collection, type, name) => {
  if (!Array.isArray(collection?.[type])) {
    return collection?.[type];
  }

  return collection[type].filter((item) =>
    typeof item === "string" ? item !== name : item?.name !== name,
  );
};

const hasCollectionMetadataByName = (collection, type, name) => {
  if (!Array.isArray(collection?.[type])) {
    return false;
  }

  return collection[type].some((item) =>
    typeof item === "string" ? item === name : item?.name === name,
  );
};

const addCollectionMetadataByName = (collection, type, name) => {
  const items = Array.isArray(collection?.[type]) ? collection[type] : [];

  if (hasCollectionMetadataByName(collection, type, name)) {
    return items;
  }

  return [...items, { name }];
};

export const getCategoriesBuilder = (builder) => {
  builder.addCase(getCategories.pending, (state) => {
    state.categories.loading = true;
    state.categories.error = null;
  });
  builder.addCase(getCategories.fulfilled, (state, { payload }) => {
    state.categories.data = payload;
    state.categories.loading = false;
    state.categories.error = null;
  });
  builder.addCase(getCategories.rejected, (state) => {
    state.categories.loading = false;
    state.categories.error = true;
  });
};

export const getCollectionsBuilder = (builder) => {
  builder.addCase(getCollections.pending, (state, { meta }) => {
    const preserveData = meta.arg?.preserveData;
    const appendMode = meta.arg?.append === true && +meta.arg?.page > 1;

    /* Three modes:
       1. append (feed view next-page) — keep data, just flag loading
       2. preserveData (background sync of current page) — keep data
       3. default (page change in grid) — clear data so a skeleton renders */
    if (appendMode || preserveData) {
      state.collections.loading = true;
    } else {
      state.collections.loading = true;
      state.collections.data = null;
    }
  });
  builder.addCase(getCollections.fulfilled, (state, { payload, meta }) => {
    const { data, page, perPage, maxReached, total } = payload;
    const preserveData = meta.arg?.preserveData;
    const appendMode = meta.arg?.append === true && +page > 1;

    if (appendMode && Array.isArray(state.collections.data)) {
      // Feed-view infinite scroll: merge new page after existing items
      state.collections.data = mergeUniqueById(state.collections.data, data);
    } else if (preserveData && Array.isArray(state.collections.data)) {
      const firstCollectionDate = Date.parse(
        state.collections.data[0]?.createdAt ?? 0,
      );
      const existingIndexById = new Map(
        state.collections.data.map((collection, index) => [
          collection._id,
          index,
        ]),
      );
      const newCollections = [];

      data.forEach((collection) => {
        const existingIndex = existingIndexById.get(collection._id);

        if (existingIndex === undefined) {
          const collectionDate = Date.parse(collection?.createdAt ?? 0);

          if (collectionDate > firstCollectionDate) {
            newCollections.push(collection);
          }
          return;
        }

        if (
          state.collections.data[existingIndex]?.status !== collection.status
        ) {
          state.collections.data[existingIndex] = collection;
        }
      });

      if (newCollections?.length) {
        state.collections.data.splice(0, 0, ...newCollections);
      }
    } else {
      state.collections.data = data;
    }

    state.collections.maxReached = maxReached;
    state.collections.perPage = +perPage;
    state.collections.page = +page;
    state.collections.total = +total;
    state.collections.loading = false;
    state.collections.hasDataBeenFetched = true;
  });
  builder.addCase(getCollections.rejected, (state) => {
    state.collections.loading = false;
    state.collections.error = true;
  });
};

const mergeUniqueById = (existing, incoming) => {
  const seen = new Set();
  const merged = [];
  for (const item of existing) {
    if (item && item._id && !seen.has(item._id)) {
      seen.add(item._id);
      merged.push(item);
    }
  }
  for (const item of incoming) {
    if (item && item._id && !seen.has(item._id)) {
      seen.add(item._id);
      merged.push(item);
    }
  }
  return merged;
};

export const getUserFavoritesBuilder = (builder) => {
  builder.addCase(getUserFavorites.pending, (state, { meta }) => {
    const appendMode = meta.arg?.append === true && +meta.arg?.page > 1;
    state.favoriteCollections.loading = true;
    state.favoriteCollections.error = null;
    if (!appendMode) {
      state.favoriteCollections.data = [];
    }
  });
  builder.addCase(getUserFavorites.fulfilled, (state, { payload, meta }) => {
    const { data, page, perPage, maxReached, total } = payload;
    const appendMode = meta.arg?.append === true && +page > 1;

    if (appendMode && Array.isArray(state.favoriteCollections.data)) {
      state.favoriteCollections.data = mergeUniqueById(
        state.favoriteCollections.data,
        data,
      );
    } else {
      state.favoriteCollections.data = data;
    }

    state.favoriteCollections.maxReached = maxReached;
    state.favoriteCollections.perPage = +perPage;
    state.favoriteCollections.page = +page;
    state.exploreCollections.page = +page;
    state.favoriteCollections.total = +total;
    state.favoriteCollections.loading = false;
    state.favoriteCollections.error = null;
    state.favoriteCollections.hasDataBeenFetched = true;
  });
  builder.addCase(getUserFavorites.rejected, (state) => {
    state.favoriteCollections.loading = false;
    state.favoriteCollections.error = true;
  });
};

export const getExploreCollectionsBuilder = (builder) => {
  builder.addCase(getExploreCollections.pending, (state, { meta }) => {
    const appendMode = meta.arg?.append === true && +meta.arg?.page > 1;
    state.exploreCollections.loading = true;
    state.exploreCollections.error = null;
    if (!appendMode) {
      state.exploreCollections.data = [];
    }
    state.exploreCollections.page = meta.arg.page;
  });

  builder.addCase(
    getExploreCollections.fulfilled,
    (state, { payload, meta }) => {
      const { data, page, perPage, maxReached, total, categories } = payload;
      const appendMode = meta.arg?.append === true && +page > 1;

      if (!state.categories.data || !state.categories.data?.length) {
        state.categories.data = categories;
      }

      if (appendMode && Array.isArray(state.exploreCollections.data)) {
        state.exploreCollections.data = mergeUniqueById(
          state.exploreCollections.data,
          data,
        );
      } else {
        state.exploreCollections.data = data;
      }

      state.exploreCollections.maxReached = maxReached;
      state.exploreCollections.perPage = +perPage;
      state.exploreCollections.page = +page;
      state.exploreCollections.total = +total;
      state.exploreCollections.loading = false;
      state.exploreCollections.error = null;
      state.exploreCollections.hasDataBeenFetched = true;
    },
  );
  builder.addCase(getExploreCollections.rejected, (state) => {
    state.exploreCollections.loading = false;
    state.exploreCollections.error = true;
  });
};

export const generateCollectionBuilder = (builder) => {
  builder.addCase(generateCollection.pending, (state) => {
    state.generateCollection.loading = true;
  });
  builder.addCase(generateCollection.fulfilled, (state, { payload }) => {
    const { collection } = payload.data;

    if (collection) {
      state.collections.page = 1;
      if (state.collections.data) {
        state.collections.data = [collection, ...state.collections.data];
      } else {
        state.collections.data = [collection];
      }
    }

    state.generateCollection.loading = false;
  });
  builder.addCase(generateCollection.rejected, (state) => {
    state.generateCollection.loading = false;
    state.generateCollection.error = true;
  });
};

export const enchantPromptBuilder = (builder) => {
  builder.addCase(enchantPrompt.pending, (state) => {
    state.enchantPromptPending = true;
  });
  builder.addCase(enchantPrompt.fulfilled, (state) => {
    state.enchantPromptPending = false;
  });
  builder.addCase(enchantPrompt.rejected, (state) => {
    state.enchantPromptPending = false;
  });
};

export const generateImageCollectionBuilder = (builder) => {
  builder.addCase(generateImageCollection.pending, (state) => {
    state.generateImageCollectionPending = true;
  });
  builder.addCase(generateImageCollection.fulfilled, (state, { payload }) => {
    const { collection } = payload.data;

    if (collection) {
      state.collections.page = 1;
      if (state.collections.data) {
        state.collections.data = [collection, ...state.collections.data];
      } else {
        state.collections.data = [collection];
      }
    }

    state.generateImageCollectionPending = false;
  });
  builder.addCase(generateImageCollection.rejected, (state) => {
    state.generateImageCollectionPending = false;
  });
};

export const regenerateCollectionBuilder = (builder) => {
  builder.addCase(regenerateCollection.pending, (state) => {
    state.regenerateCollectionPending = true;
  });
  builder.addCase(regenerateCollection.fulfilled, (state, { payload }) => {
    const { collection } = payload.data;

    if (collection) {
      state.collections.page = 1;
      if (state.collections.data) {
        state.collections.data = [collection, ...state.collections.data];
      } else {
        state.collections.data = [collection];
      }
    }

    state.regenerateCollectionPending = false;
  });
  builder.addCase(regenerateCollection.rejected, (state) => {
    state.regenerateCollectionPending = false;
  });
};

export const regenerateImageCollectionBuilder = (builder) => {
  builder.addCase(regenerateImageCollection.pending, (state) => {
    state.regenerateImageCollectionPending = true;
  });
  builder.addCase(regenerateImageCollection.fulfilled, (state, { payload }) => {
    const { collection } = payload.data;

    if (collection) {
      state.collections.page = 1;
      if (state.collections.data) {
        state.collections.data = [collection, ...state.collections.data];
      } else {
        state.collections.data = [collection];
      }
    }

    state.regenerateImageCollectionPending = false;
  });
  builder.addCase(regenerateImageCollection.rejected, (state) => {
    state.regenerateImageCollectionPending = false;
  });
};

export const generateAnonymizeBuilder = (builder) => {
  builder.addCase(generateAnonymize.pending, (state) => {
    state.generateAnonymizedCollectionPending = true;
  });
  builder.addCase(generateAnonymize.fulfilled, (state, { payload }) => {
    state.generateAnonymizedCollectionPending = false;

    state.anonymizedCollections.data = [
      payload,
      ...state.anonymizedCollections.data,
    ];
  });
  builder.addCase(generateAnonymize.rejected, (state) => {
    state.generateAnonymizedCollectionPending = false;
  });
};

export const getAnonymizedCollectionsBuilder = (builder) => {
  builder.addCase(fetchAnonymizedCollections.pending, (state) => {
    state.anonymizedCollections.loading = true;
  });
  builder.addCase(
    fetchAnonymizedCollections.fulfilled,
    (state, { payload }) => {
      state.anonymizedCollections.loading = false;

      state.anonymizedCollections.data = payload;
    },
  );
  builder.addCase(fetchAnonymizedCollections.rejected, (state) => {
    state.anonymizedCollections.loading = false;
  });
};

export const selectCollectionsInCollectionGroupBuilder = (builder) => {
  builder.addCase(selectCollectionsInCollectionGroup.pending, (state) => {
    state.selectCollectionsInCollectionGroupPending = true;
  });
  builder.addCase(
    selectCollectionsInCollectionGroup.fulfilled,
    (state, { payload }) => {
      state.selectCollectionsInCollectionGroupPending = false;
      state.anonymizedCollections.data = [
        ...payload.collections,
        ...state.anonymizedCollections.data.filter(
          (el) =>
            el.type !== COLLECTION_GROUP_TYPE.ANONYMISE_IMAGE ||
            (el.type === COLLECTION_GROUP_TYPE.ANONYMISE_IMAGE &&
              el._id !== payload.group),
        ),
      ];
    },
  );
  builder.addCase(selectCollectionsInCollectionGroup.rejected, (state) => {
    state.selectCollectionsInCollectionGroupPending = false;
  });
};

export const deleteCollectionBuilder = (builder) => {
  builder.addCase(deleteCollection.pending, (state) => {
    state.deleteCollectionPending = true;
  });
  builder.addCase(deleteCollection.fulfilled, (state, { payload }) => {
    const { ids } = payload;

    state.collections.data = state.collections.data.filter(
      (collection) => !ids.includes(collection._id),
    );

    state.deleteCollectionPending = false;
  });
  builder.addCase(deleteCollection.rejected, (state) => {
    state.deleteCollectionPending = false;
  });
};

export const hideCollectionBuilder = (builder) => {
  builder.addCase(hideCollection.pending, (state) => {
    state.hideCollectionPending = true;
  });
  builder.addCase(hideCollection.fulfilled, (state, { payload }) => {
    const { collectionId, hide } = payload;

    updateCollectionInList(
      state.collections.data,
      collectionId,
      (collection) => ({
        ...collection,
        isPublic: !hide,
      }),
    );

    if (state.selectedCollection?._id === collectionId) {
      state.selectedCollection = {
        ...state.selectedCollection,
        isPublic: !hide,
      };
    }

    if (state.collectionDetails.data?._id === collectionId) {
      state.collectionDetails.data = {
        ...state.collectionDetails.data,
        isPublic: !hide,
      };
    }

    state.hideCollectionPending = false;
  });
  builder.addCase(hideCollection.rejected, (state) => {
    state.hideCollectionPending = false;
  });
};

export const hideCollectionVipBuilder = (builder) => {
  builder.addCase(hideCollectionVip.pending, (state) => {
    state.hideCollectionVipPending = true;
  });
  builder.addCase(hideCollectionVip.fulfilled, (state, { payload }) => {
    const { collectionId, hide } = payload;
    const updateCollection = (collection) => ({
      ...collection,
      categories: hide
        ? removeCollectionMetadataByName(
            collection,
            "categories",
            "VIP Creators",
          )
        : addCollectionMetadataByName(collection, "categories", "VIP Creators"),
      tags: hide
        ? removeCollectionMetadataByName(collection, "tags", "VIP")
        : addCollectionMetadataByName(collection, "tags", "VIP"),
    });

    updateCollectionInList(
      state.collections.data,
      collectionId,
      updateCollection,
    );

    if (state.selectedCollection?._id === collectionId) {
      state.selectedCollection = updateCollection(state.selectedCollection);
    }

    if (state.collectionDetails.data?._id === collectionId) {
      state.collectionDetails.data = updateCollection(
        state.collectionDetails.data,
      );
    }

    if (
      state.selectedTrainModel.data?.selectedCollection?._id === collectionId
    ) {
      state.selectedTrainModel.data = {
        ...state.selectedTrainModel.data,
        addToVipCategory: !hide,
        selectedCollection: updateCollection(
          state.selectedTrainModel.data.selectedCollection,
        ),
      };
    }

    state.hideCollectionVipPending = false;
  });
  builder.addCase(hideCollectionVip.rejected, (state) => {
    state.hideCollectionVipPending = false;
  });
};

export const getTrainModelsBuilder = (builder) => {
  builder.addCase(getTrainModels.pending, (state) => {
    state.trainModels.loading = true;
  });
  builder.addCase(getTrainModels.fulfilled, (state, { payload }) => {
    state.trainModels.data = payload.data;
    state.trainModels.page = payload.page;
    state.trainModels.total = payload.total;
    state.trainModels.loading = false;
    state.trainModels.hasDataBeenFetched = true;
  });
  builder.addCase(getTrainModels.rejected, (state) => {
    state.trainModels.loading = false;
    state.trainModels.error = true;
  });
};

export const getTrainModelBuilder = (builder) => {
  builder.addCase(getTrainModel.pending, (state) => {
    state.selectedTrainModel.loading = true;
  });
  builder.addCase(getTrainModel.fulfilled, (state, { payload }) => {
    const { data } = payload;
    state.selectedTrainModel.data = data.trainModel;
    state.selectedTrainModel.loading = false;
    state.selectedTrainModel.trainModelId = data.trainModel._id;
  });
  builder.addCase(getTrainModel.rejected, (state) => {
    state.selectedTrainModel.loading = false;
    state.selectedTrainModel.error = true;
  });
};

export const fetchCollectionBuilder = (builder) => {
  builder.addCase(fetchCollection.pending, (state) => {
    state.collectionDetails.loading = true;
  });
  builder.addCase(fetchCollection.fulfilled, (state, { payload }) => {
    const { data } = payload;
    state.collectionDetails.data = data;
    state.collectionDetails.loading = false;
  });
  builder.addCase(fetchCollection.rejected, (state) => {
    state.collectionDetails.loading = false;
    state.collectionDetails.data = null;
    state.collectionDetails.error = true;
  });
};

export const createTrainModelBuilder = (builder) => {
  builder.addCase(createTrainModel.pending, (state) => {
    state.createTrainModel.loading = true;
  });
  builder.addCase(createTrainModel.fulfilled, (state, { payload }) => {
    const trainModel = payload.data;

    if (trainModel) {
      state.trainModels.data = [trainModel, ...state.trainModels.data];
    }

    state.createTrainModel.loading = false;
  });
  builder.addCase(createTrainModel.rejected, (state) => {
    state.createTrainModel.loading = false;
    state.createTrainModel.error = true;
  });
};

export const updateTrainModelBuilder = (builder) => {
  builder.addCase(updateTrainModel.pending, (state) => {
    state.updateTrainModel.loading = true;
  });
  builder.addCase(updateTrainModel.fulfilled, (state, { payload }) => {
    const { trainModel } = payload.data;

    state.trainModels.data = state.trainModels.data.map((model) =>
      model._id === trainModel._id ? trainModel : model,
    );

    if (
      state.selectedTrainModel.data &&
      state.selectedTrainModel.data._id === trainModel._id
    ) {
      state.selectedTrainModel.data = trainModel;
    }

    state.updateTrainModel.loading = false;
  });
  builder.addCase(updateTrainModel.rejected, (state) => {
    state.updateTrainModel.loading = false;
    state.updateTrainModel.error = true;
  });
};

export const deleteTrainModelBuilder = (builder) => {
  builder.addCase(deleteTrainModel.pending, (state) => {
    state.deleteTrainModelPending = true;
  });
  builder.addCase(deleteTrainModel.fulfilled, (state, { payload }) => {
    const { id } = payload;

    state.trainModels.data = state.trainModels.data.filter(
      (trainModel) => trainModel._id !== id,
    );

    state.deleteTrainModelPending = false;
  });
  builder.addCase(deleteTrainModel.rejected, (state) => {
    state.deleteTrainModelPending = false;
  });
};

export const getTopCreatorsBuilder = (builder) => {
  builder.addCase(fetchTopCreators.pending, (state) => {
    state.getTopCreatorsPending = true;
  });
  builder.addCase(fetchTopCreators.fulfilled, (state, { payload }) => {
    const { data } = payload;

    state.topCreators = data;
    state.getTopCreatorsPending = false;
  });
  builder.addCase(fetchTopCreators.rejected, (state) => {
    state.getTopCreatorsPending = false;
  });
};

export const rateModelBuilder = (builder) => {
  builder.addCase(rateModel.pending, (state, { meta }) => {
    const { collectionId, rating } = meta.arg;
    state.collections.data = state.collections.data.map((item) =>
      item._id === collectionId ? { ...item, rating } : item,
    );

    state.rateModelPending = true;
  });
  builder.addCase(rateModel.fulfilled, (state, { payload }) => {
    state.rateModelPending = false;
  });
  builder.addCase(rateModel.rejected, (state) => {
    state.rateModelPending = false;
  });
};

export const getDefaultGalleryBuilder = (builder) => {
  builder.addCase(fetchDefaultGallery.pending, (state) => {
    state.getDefaultGalleryPending = true;
  });
  builder.addCase(fetchDefaultGallery.fulfilled, (state, { payload }) => {
    const data = payload;

    state.gallery.data = data;
    state.getDefaultGalleryPending = false;
  });
  builder.addCase(fetchDefaultGallery.rejected, (state) => {
    state.getDefaultGalleryPending = false;
  });
};

export const selectCoverForTrainModelBuilder = (builder) => {
  builder.addCase(selectCoverForTrainModel.pending, (state) => {
    state.selectCoverForTrainModelPending = true;
  });
  builder.addCase(selectCoverForTrainModel.fulfilled, (state, { payload }) => {
    state.selectCoverForTrainModelPending = false;
  });
  builder.addCase(selectCoverForTrainModel.rejected, (state) => {
    state.selectCoverForTrainModelPending = false;
  });
};

export const setCollectionPublicCoverBuilder = (builder) => {
  builder.addCase(setCollectionPublicCover.pending, (state) => {
    state.setCollectionPublicCoverPending = true;
  });
  builder.addCase(setCollectionPublicCover.fulfilled, (state) => {
    state.setCollectionPublicCoverPending = false;
  });
  builder.addCase(setCollectionPublicCover.rejected, (state) => {
    state.setCollectionPublicCoverPending = false;
  });
};

export const submitTrainModelForReviewBuilder = (builder) => {
  builder.addCase(submitTrainModelForReview.pending, (state) => {
    state.submitTrainModelForReviewPending = true;
  });
  builder.addCase(submitTrainModelForReview.fulfilled, (state, { payload }) => {
    state.selectedTrainModel.data.status =
      TRAIN_MODEL_STATUS.SUBMITTED_FOR_REVIEW;
    state.submitTrainModelForReviewPending = false;
  });
  builder.addCase(submitTrainModelForReview.rejected, (state) => {
    state.submitTrainModelForReviewPending = false;
  });
};

export const getCollectionFoldersBuilder = (builder) => {
  builder.addCase(getFolders.pending, (state) => {
    state.collectionFolders.loading = true;
  });
  builder.addCase(getFolders.fulfilled, (state, { payload }) => {
    state.collectionFolders.data = payload;
    state.collectionFolders.loading = false;
  });
  builder.addCase(getFolders.rejected, (state) => {
    state.collectionFolders.loading = false;
  });
};

export const createCollectionFolderBuilder = (builder) => {
  builder.addCase(createFolder.pending, (state) => {
    state.createFolderPending = true;
  });
  builder.addCase(createFolder.fulfilled, (state, { payload }) => {
    state.collectionFolders.data = [...state.collectionFolders.data, payload];
    state.createFolderPending = false;
  });
  builder.addCase(createFolder.rejected, (state) => {
    state.createFolderPending = false;
  });
};

export const deleteCollectionFolderBuilder = (builder) => {
  builder.addCase(deleteFolder.pending, (state) => {
    state.deleteFolderPending = true;
  });
  builder.addCase(deleteFolder.fulfilled, (state, { payload }) => {
    state.collectionFolders.data = state.collectionFolders.data.filter(
      (el) => el.name !== payload.name,
    );
    state.deleteFolderPending = false;
  });
  builder.addCase(deleteFolder.rejected, (state) => {
    state.deleteFolderPending = false;
  });
};

export const editCollectionFolderBuilder = (builder) => {
  builder.addCase(editFolder.pending, (state) => {
    state.editFolderPending = true;
  });

  builder.addCase(editFolder.fulfilled, (state, { payload }) => {
    state.editFolderPending = false;

    if (
      state.collectionFolders.data &&
      Array.isArray(state.collectionFolders.data)
    ) {
      const folderIndex = state.collectionFolders.data.findIndex(
        (folder) => folder._id === payload._id,
      );

      if (folderIndex !== -1) {
        state.collectionFolders.data[folderIndex] = payload;
      }
    }
  });

  builder.addCase(editFolder.rejected, (state) => {
    state.editFolderPending = false;
  });
};

export const putCollectionToFolderBuilder = (builder) => {
  builder.addCase(putCollectionToFolder.pending, (state) => {
    state.putCollectionToFolderPending = true;
  });

  builder.addCase(
    putCollectionToFolder.fulfilled,
    (state, { payload, meta }) => {
      state.putCollectionToFolderPending = false;

      if (state.collections.data && Array.isArray(state.collections.data)) {
        const collectionIndex = state.collections.data.findIndex(
          (c) => c._id === payload._id,
        );

        if (collectionIndex !== -1) {
          const currentFolderId = meta.arg.currentFolderId;

          if (
            currentFolderId &&
            currentFolderId !== "ALL" &&
            currentFolderId !== "FAVORITES" &&
            !payload.folders.includes(currentFolderId)
          ) {
            state.collections.data.splice(collectionIndex, 1);
            state.collections.total = Math.max(0, state.collections.total - 1);
          } else {
            state.collections.data[collectionIndex] = payload;
          }
        }
      }
    },
  );

  builder.addCase(putCollectionToFolder.rejected, (state) => {
    state.putCollectionToFolderPending = false;
  });
};

export const toggleToFavoriteBuilder = (builder) => {
  builder.addCase(toggleToFavorite.pending, (state) => {
    state.toggleToFavoritePending = true;
  });

  builder.addCase(toggleToFavorite.fulfilled, (state, { payload, meta }) => {
    state.toggleToFavoritePending = false;

    const collectionId = payload;
    const currentFolderId = meta.arg.currentFolderId;

    if (state.collections.data && Array.isArray(state.collections.data)) {
      const existingIndex = state.collections.data.findIndex(
        (collection) => collection._id === collectionId,
      );

      if (currentFolderId === "FAVORITES") {
        if (existingIndex !== -1) {
          state.collections.data.splice(existingIndex, 1);
          state.collections.total = Math.max(0, state.collections.total - 1);
        } else {
          state.collections.data[existingIndex] = payload;
        }
      }
    }
  });

  builder.addCase(toggleToFavorite.rejected, (state) => {
    state.toggleToFavoritePending = false;
  });
};
